export const PUBLIC_RUNTIME_CONFIG_SCRIPT_ID = "mlx-public-runtime-config";

export type PublicRuntimeConfig = {
  schemaVersion: 1;
  appUrl?: string;
  tenantSlug?: string;
  deploymentTarget?: string;
  server?: string;
  environment?: string;
  release?: string;
  clerkPublishableKey?: string;
  postHogKey?: string;
  postHogHost?: string;
  sentryDsn?: string;
  sentryTracesSampleRate?: string;
  hiddenSurfaces?: string;
  inboxAllowedEmailDomains?: string;
  workflowsAllowedEmailDomains?: string;
  orgAvatarBackgroundColor?: string;
  orgAvatarTextColor?: string;
  venusThemeEnabled?: string;
  editorHostBaseUrl?: string;
  editorHostDocPath?: string;
  editorHosts: Record<string, { baseUrl?: string; docPath?: string }>;
};

const optionalStringFields = [
  "appUrl",
  "tenantSlug",
  "deploymentTarget",
  "server",
  "environment",
  "release",
  "clerkPublishableKey",
  "postHogKey",
  "postHogHost",
  "sentryDsn",
  "sentryTracesSampleRate",
  "hiddenSurfaces",
  "inboxAllowedEmailDomains",
  "workflowsAllowedEmailDomains",
  "orgAvatarBackgroundColor",
  "orgAvatarTextColor",
  "venusThemeEnabled",
  "editorHostBaseUrl",
  "editorHostDocPath",
] as const;

let browserConfig: PublicRuntimeConfig | undefined;

export function publicRuntimeConfig(
  env?: Record<string, string | undefined>,
): PublicRuntimeConfig {
  if (env) return publicRuntimeConfigFromEnv(env);
  if (typeof document !== "undefined") {
    if (browserConfig) return browserConfig;
    const element = document.getElementById(PUBLIC_RUNTIME_CONFIG_SCRIPT_ID);
    if (!element?.textContent) {
      console.error("[runtime-config] public runtime configuration is missing");
      return emptyPublicRuntimeConfig();
    }
    browserConfig = parsePublicRuntimeConfig(element.textContent);
    return browserConfig;
  }
  return publicRuntimeConfigFromEnv(
    typeof process === "undefined" ? {} : process.env,
  );
}

export function publicRuntimeConfigFromEnv(
  env: Record<string, string | undefined>,
): PublicRuntimeConfig {
  const editorHosts: PublicRuntimeConfig["editorHosts"] = {};
  for (const [name, rawValue] of Object.entries(env)) {
    const match = name.match(
      /^NEXT_PUBLIC_HOST_(BASE_URL|DOC_PATH)_([A-Z0-9_]+)$/u,
    );
    const value = optionalValue(rawValue);
    if (!match || !value) continue;
    const host = editorHosts[match[2]!] ?? {};
    if (match[1] === "BASE_URL") host.baseUrl = value;
    else host.docPath = value;
    editorHosts[match[2]!] = host;
  }

  return compactConfig({
    schemaVersion: 1,
    appUrl: env.MLX_APP_PUBLIC_URL,
    tenantSlug: env.MLX_APP_TENANT_SLUG,
    deploymentTarget: env.MLX_TARGET,
    server: env.REMOTE_HOST,
    environment: env.NODE_ENV,
    release: env.MLX_DEPLOYMENT_RELEASE ?? env.SENTRY_RELEASE,
    clerkPublishableKey: env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    postHogKey: env.NEXT_PUBLIC_POSTHOG_KEY,
    postHogHost: env.NEXT_PUBLIC_POSTHOG_HOST,
    sentryDsn: env.SENTRY_DSN,
    sentryTracesSampleRate: env.SENTRY_TRACES_SAMPLE_RATE,
    hiddenSurfaces: env.NEXT_PUBLIC_MLX_HIDDEN_SURFACES,
    inboxAllowedEmailDomains: env.NEXT_PUBLIC_MLX_INBOX_ALLOWED_EMAIL_DOMAINS,
    workflowsAllowedEmailDomains:
      env.NEXT_PUBLIC_MLX_WORKFLOWS_ALLOWED_EMAIL_DOMAINS,
    orgAvatarBackgroundColor: env.NEXT_PUBLIC_MLX_ORG_AVATAR_BG_COLOR,
    orgAvatarTextColor: env.NEXT_PUBLIC_MLX_ORG_AVATAR_TEXT_COLOR,
    venusThemeEnabled: env.NEXT_PUBLIC_MLX_VENUS_THEME_ENABLED,
    editorHostBaseUrl: env.NEXT_PUBLIC_HOST_BASE_URL,
    editorHostDocPath: env.NEXT_PUBLIC_HOST_DOC_PATH,
    editorHosts,
  });
}

export function serializePublicRuntimeConfig(
  config: PublicRuntimeConfig,
): string {
  return JSON.stringify(config)
    .replaceAll("<", "\\u003c")
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}

export function parsePublicRuntimeConfig(
  serialized: string,
): PublicRuntimeConfig {
  const parsed: unknown = JSON.parse(serialized);
  if (!isRecord(parsed) || parsed.schemaVersion !== 1) {
    throw new Error("Public runtime configuration has an unsupported schema.");
  }
  const editorHosts = parseEditorHosts(parsed.editorHosts);
  const config: PublicRuntimeConfig = { schemaVersion: 1, editorHosts };
  for (const field of optionalStringFields) {
    const value = parsed[field];
    if (value !== undefined && typeof value !== "string") {
      throw new Error(
        `Public runtime configuration field ${field} must be a string.`,
      );
    }
    const normalized = optionalValue(value);
    if (normalized) config[field] = normalized;
  }
  return config;
}

function compactConfig(config: PublicRuntimeConfig): PublicRuntimeConfig {
  for (const field of optionalStringFields) {
    const value = optionalValue(config[field]);
    if (value) config[field] = value;
    else delete config[field];
  }
  return config;
}

function parseEditorHosts(value: unknown): PublicRuntimeConfig["editorHosts"] {
  if (!isRecord(value)) {
    throw new Error(
      "Public runtime editor host configuration must be an object.",
    );
  }
  const hosts: PublicRuntimeConfig["editorHosts"] = {};
  for (const [name, candidate] of Object.entries(value)) {
    if (!/^[A-Z0-9_]+$/u.test(name) || !isRecord(candidate)) {
      throw new Error("Public runtime editor host configuration is invalid.");
    }
    const baseUrl = optionalValue(candidate.baseUrl);
    const docPath = optionalValue(candidate.docPath);
    if (baseUrl || docPath) {
      hosts[name] = {
        ...(baseUrl ? { baseUrl } : {}),
        ...(docPath ? { docPath } : {}),
      };
    }
  }
  return hosts;
}

function emptyPublicRuntimeConfig(): PublicRuntimeConfig {
  return { schemaVersion: 1, editorHosts: {} };
}

function optionalValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
